import { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fmtINR } from "@/lib/api";
import { RevenueGoal } from "@/components/admin/RevenueGoal";

const iso = (d) => d.toISOString().slice(0, 10);
const defaultRange = () => {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  return { start: iso(new Date(start.getTime() - start.getTimezoneOffset() * 60000)), end: iso(end) };
};
const short = (v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : v >= 1000 ? `₹${+(v / 1000).toFixed(1)}k` : `₹${v}`);

const toCsv = (d) => {
  const rows = [["Section", "Label", "Count", "Enrollments INR", "Orders INR", "Total INR", "Target INR"]];
  d.monthly.forEach((m) => rows.push(["Monthly", m.month, "", m.enrollments_inr, m.orders_inr, m.total_inr, m.target_inr ?? ""]));
  d.programs.forEach((p) => rows.push(["Program", p.program, p.count, p.revenue_inr, "", p.revenue_inr]));
  d.top_kits.forEach((k) => rows.push(["Top kit", k.name, k.qty, "", k.revenue_inr, k.revenue_inr]));
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
};

const Card = ({ title, testid, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
    <h3 className="font-['Outfit'] font-bold text-[#0A192F] mb-4">{title}</h3>
    {children}
  </div>
);

const Empty = ({ testid, text }) => (
  <div className="h-64 grid place-items-center text-sm text-slate-500" data-testid={testid}>{text}</div>
);

const Totals = ({ t }) => (
  <div className="grid grid-cols-3 gap-3">
    {[["Revenue", fmtINR(t.revenue_inr), "revenue-total"], ["Paid enrollments", t.enrollments, "revenue-enrollments-count"], ["Kits sold", t.kits_sold, "revenue-kits-count"]].map(([l, v, id]) => (
      <div key={id} className="bg-[#0A192F] text-white rounded-xl p-4" data-testid={id}>
        <div className="text-xs uppercase tracking-widest text-slate-300">{l}</div>
        <div className="text-2xl font-extrabold font-['Outfit'] mt-1">{v}</div>
      </div>
    ))}
  </div>
);

const MonthlyChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <LineChart data={data}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
      <XAxis dataKey="month" fontSize={12} />
      <YAxis tickFormatter={short} fontSize={12} width={60} />
      <Tooltip formatter={(v) => fmtINR(v)} />
      <Legend />
      <Line type="monotone" dataKey="total_inr" name="Total" stroke="#FF3B3B" strokeWidth={3} dot={{ r: 3 }} />
      <Line type="monotone" dataKey="enrollments_inr" name="Enrollments" stroke="#0A192F" strokeWidth={2} />
      <Line type="monotone" dataKey="orders_inr" name="Roboshop" stroke="#00B3A4" strokeWidth={2} />
      <Line type="stepAfter" dataKey="target_inr" name="Target" stroke="#F59E0B" strokeDasharray="6 4" strokeWidth={2} dot={false} connectNulls={false} />
    </LineChart>
  </ResponsiveContainer>
);

const HBar = ({ data, dataKey, nameKey, color, fmt }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
      <XAxis type="number" fontSize={12} allowDecimals={false} />
      <YAxis type="category" dataKey={nameKey} width={130} fontSize={12} />
      <Tooltip formatter={fmt} />
      <Bar dataKey={dataKey} fill={color} radius={[0, 6, 6, 0]} maxBarSize={36} />
    </BarChart>
  </ResponsiveContainer>
);

export const RevenueDashboard = ({ auth, onErr }) => {
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/revenue", { ...auth, params: range })
      .then((r) => setData(r.data))
      .catch((e) => { onErr(e); toast.error(e.response?.data?.detail || "Could not load revenue"); })
      .finally(() => setLoading(false));
  }, [auth, range, onErr]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(data)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `robospark-revenue-${data.start}-to-${data.end}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-6 space-y-5" data-testid="revenue-dashboard">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-slate-600">From
          <Input type="date" value={range.start} max={range.end} onChange={(e) => e.target.value && setRange((r) => ({ ...r, start: e.target.value }))} className="bg-white mt-1" data-testid="revenue-start-date" />
        </label>
        <label className="text-xs font-semibold text-slate-600">To
          <Input type="date" value={range.end} min={range.start} onChange={(e) => e.target.value && setRange((r) => ({ ...r, end: e.target.value }))} className="bg-white mt-1" data-testid="revenue-end-date" />
        </label>
        <Button variant="outline" onClick={() => setRange(defaultRange())} data-testid="revenue-reset-range">Last 12 months</Button>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="revenue-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
        <Button onClick={exportCsv} disabled={!data} className="bg-[#FF3B3B] hover:bg-[#e63232] text-white" data-testid="revenue-export-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
      </div>
      {data && <>
        <Totals t={data.totals} />
        <RevenueGoal monthly={data.monthly} auth={auth} onSaved={load} />
        <Card title="Monthly revenue" testid="revenue-monthly-chart"><MonthlyChart data={data.monthly} /></Card>
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Paid enrollments by program" testid="revenue-programs-chart">
            {data.programs.length ? <HBar data={data.programs} dataKey="count" nameKey="program" color="#0A192F" fmt={(v) => `${v} enrollments`} />
              : <Empty testid="revenue-programs-empty" text="No paid enrollments in this range" />}
          </Card>
          <Card title="Top 5 Roboshop kits" testid="revenue-kits-chart">
            {data.top_kits.length ? <HBar data={data.top_kits} dataKey="qty" nameKey="name" color="#00B3A4" fmt={(v) => `${v} sold`} />
              : <Empty testid="revenue-kits-empty" text="No paid Roboshop orders in this range" />}
          </Card>
        </div>
      </>}
    </div>
  );
};
